import { useEditContext } from '@components/utils/contexts/EditContext';
import { ROUTE_PRODUCT } from '@constants';
import { useEmbedContext } from '@lib/embed/providers/EmbedProvider';
import { useRouter } from 'next/router';
import {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
} from 'react';

import {
    useProgressContext,
    useProgressDispatchContext,
} from './ProgressContext';
import { useStoryContext } from './StoryContext';

interface NavigationContext {
    next: () => void;
    previous: () => void;
    canGoForward: boolean;
    canGoBack: boolean;
}

const partialInitialContext: NavigationContext = {
    next: () => {},
    previous: () => {},
    canGoForward: false,
    canGoBack: false,
};

export const NavigationContext = createContext<NavigationContext>(
    partialInitialContext
);

export const NavigationProvider: FC<PropsWithChildren> = ({ children }) => {
    const router = useRouter();

    const {
        pathname,
        query: { company, product },
    } = router;

    const { isEditMode } = useEditContext();
    const { isEmbedContextLoading, isStoryBundle } = useEmbedContext();
    const { module, story, storyPages, previousStory, nextStory } =
        useStoryContext();
    const { currentPageIndex, numberOfPages } = useProgressContext();
    const dispatch = useProgressDispatchContext();

    const canEscapeStory = useMemo(() => {
        return !isEditMode && !isEmbedContextLoading && !isStoryBundle;
    }, [isEditMode, isEmbedContextLoading, isStoryBundle]);

    const isFirstStoryPage = useMemo(
        () => currentPageIndex === 0,
        [currentPageIndex]
    );

    const isLastStoryPage = useMemo(
        () => currentPageIndex + 1 === numberOfPages,
        [currentPageIndex, numberOfPages]
    );

    const canGoBack = useMemo(() => {
        if (isEditMode) return false;
        if (!isFirstStoryPage) return true;
        return !!previousStory;
    }, [isEditMode, isFirstStoryPage, previousStory]);

    const canGoForward = useMemo(() => {
        if (isEditMode) return false;
        if (!isLastStoryPage) return true;
        if (nextStory) return true;
        return !!canEscapeStory;
    }, [isEditMode, isLastStoryPage, nextStory, canEscapeStory]);

    const previous = useCallback(() => {
        if (!canGoBack) return;
        if (currentPageIndex === 0 && previousStory) {
            return router.push(
                {
                    pathname,
                    query: {
                        company,
                        product,
                        module,
                        frame: -1,
                        slug: previousStory.Slug,
                    },
                },
                `/${product}/story/${previousStory.Slug}`
            );
        }

        dispatch({ type: 'PREVIOUS_PAGE' });
    }, [
        canGoBack,
        company,
        currentPageIndex,
        module,
        pathname,
        previousStory,
        product,
        router,
        dispatch,
    ]);

    const next = useCallback(() => {
        if (!canGoForward) return;
        if (!isLastStoryPage) {
            return dispatch({ type: 'NEXT_PAGE' });
        }

        if (nextStory) {
            return router.push(
                {
                    pathname,
                    query: {
                        company,
                        product,
                        module,
                        slug: nextStory.Slug,
                    },
                },
                `/${product}/story/${nextStory.Slug}#next`
            );
        }

        if (!canEscapeStory) return;

        return router.push(
            { pathname: ROUTE_PRODUCT, query: { company, product } },
            `/${product}`
        );
    }, [
        canGoForward,
        canEscapeStory,
        company,
        isLastStoryPage,
        module,
        nextStory,
        pathname,
        product,
        router,
        dispatch,
    ]);

    const initialContext = {
        ...partialInitialContext,
        module,
        story,
        storyPages,
        previousStory,
        nextStory,
        next,
        previous,
        canGoForward,
        canGoBack,
    };
    return (
        <NavigationContext.Provider value={initialContext}>
            {children}
        </NavigationContext.Provider>
    );
};

export const useNavigationContext = () =>
    useContext<NavigationContext>(NavigationContext);
